/**
 * @file dictionary.component.tsx
 * @description Компонент для работы c выбором внутреннего объекта из списка. При нажатии открывается каталог, откуда можно выбрать объект.
 * Так же есть возможность очистить значение. При клике на значении обхекта открывается форма для просмотра/редактирования объекта.
 * @author Genotroid
 * @created 2025-09-03
 * @updated 2025-09-03
 * @dependencies []
 * @related-files []
 */

import { Text } from '@components/ui/base/text';
import { useRef, useState, useMemo } from 'react';
import { IconClose } from '@consta/icons/IconClose';
import { IconWarning } from '@consta/icons/IconWarning';
import { Button } from '@components/ui/base/button';
import { getIsObjectValid, getSimpleObject } from '@utils/form-object-helper';
import { Catalog } from '@features/catalog';
import { DictionaryIcon } from '@components/icons';
import { getObjectEntity } from '@utils/form-object-helper';
import { ObjectForm } from '@src/features/object-form';
import cn from 'classnames';
import { IBaseObjectDTO, ITableItemDTO } from '@src/types/dto';
import { IBasePage } from '@src/types/page';

/**
 * Props для компонента Dictionary - селектор объектов из каталога
 */
export interface IDictionaryProps {
    /** Выбранный объект или значение поля */
    value: IBaseObjectDTO;
    /** Лейбл поля */
    label?: string;
    /** ID строки для идентификации */
    rowId?: string;
    /** Плейсхолдер отображаемый при пустом значении */
    placeholder?: string;
    /** Callback функция вызываемая при изменении значения */
    onChange?: (newValue: IBaseObjectDTO) => void;
    /** Поле только для чтения */
    readonly?: Boolean;
    /** Обязательное поле */
    required?: Boolean;
    /** Вариант отображения поля */
    view?: 'default' | 'clear';
    /** Ширина компонента в CSS единицах */
    width?: string;
    /** Заблокировано ли поле */
    isDisabled?: boolean;
    /** Отображать плейсхолдер курсивом */
    isItalicPlaceholder?: boolean;
    /** Возможность очистки значения */
    isCanClear?: boolean;
    /** Возможность поиска в каталоге */
    isCanSearch?: boolean;
    /** Контейнер для портала модальных окон */
    container?: HTMLDivElement;
    /** Текущие элементы для фильтрации каталога */
    currentItems?: ITableItemDTO[];
    /** Статус поля (для валидации) */
    status?: 'alert' | 'success' | 'warning';
}

export const Dictionary = ({
    value,
    placeholder,
    label,
    onChange,
    required = false,
    readonly = false,
    view = 'default',
    width = '100%',
    isDisabled = false,
    isItalicPlaceholder = false,
    isCanClear = true,
    isCanSearch = true,
    status,
    currentItems = [],
    container = document.getElementById('MainLayout') as HTMLDivElement
}: IDictionaryProps) => {
    const inputRef = useRef<HTMLDivElement>(null);
    const objectEntity = useMemo<IBasePage>(() => getObjectEntity(value.$type || ''), [value]);
    const [isShowObject, setIsShowObject] = useState(false);
    const [isShowCatalog, setIsShowCatalog] = useState(false);

    const onClear = () => {
        if (!value.$type) {
            return;
        }

        const newItemValue = {
            id: '',
            name: '',
            $type: value.$type
        };

        onChange && onChange(newItemValue);
    };

    const onItemClick = (row: ITableItemDTO) => {
        onChange && onChange(row.$item);
        setIsShowCatalog(false);
    };

    return (
        <div className="dictionary__component" style={{ width }}>
            {/* <Tooltip
                size="s"
                anchorRef={inputRef}
                status="alert"
                isOpen={value.error && view === 'default'}
                direction="upCenter"
            >
                <Text size="s">{value.$message}</Text>
            </Tooltip> */}
            {label && (
                <div className="flex">
                    <Text size="s" view="secondary" className={`whitespace-nowrap dictionary__label mb-[0.5283vmin]`}>
                        {label}
                    </Text>

                    {required && <span className="FieldLabel-Star">*</span>}
                </div>
            )}

            <div
                ref={inputRef}
                className={cn('dictionary__input flex flex-row bg-white', {
                    'border rounded border-[#00426947] hover:border-[#00426985] h-[3.1756vmin]': view === 'default',
                    'h-full dictionary__input_clear': view !== 'default',
                    'cursor-pointer': !isDisabled,
                    'border-alert': value.$error || status === 'alert'
                })}
                style={{ width }}
            >
                {value.id && value.name ? (
                    <Text
                        size="s"
                        className={cn(
                            'dictionary__name wrapper flex flex-1 w-full min-w-0 items-center h-full hover:text-link-hover',
                            {
                                'pl-cell-horizontal': view !== 'clear'
                            }
                        )}
                        onClick={() => {
                            if (getIsObjectValid(value)) {
                                setIsShowObject(true);
                                return;
                            }

                            if (
                                (onChange && value.name && value.id === import.meta.env.VITE_EMPTY_GUID) ||
                                value.$error
                            ) {
                                setIsShowCatalog(true);
                                return;
                            }
                        }}
                    >
                        {value.$error && view !== 'default' && <IconWarning size="s" view="alert" />}

                        <span className="TextField wrapper block">{value.name}</span>
                    </Text>
                ) : (
                    <div
                        className="flex flex-1 w-full min-w-0 items-center h-full"
                        onClick={() => {
                            if (!isDisabled && isCanSearch) {
                                setIsShowCatalog(Boolean(value));
                            }
                        }}
                    >
                        {
                            <Text
                                size="s"
                                className={cn('dictionary__placeholder h-full flex items-center', {
                                    'pl-[0.8127vmin]': view !== 'clear',
                                    'w-inherit': label || !required
                                })}
                                fontStyle={isItalicPlaceholder ? 'italic' : undefined}
                            >
                                {placeholder}
                            </Text>
                        }
                        {(view === 'clear' || !label) && required && <span className="FieldLabel-Star">*</span>}
                    </div>
                )}

                {isCanClear && !readonly && !isDisabled && value.id && value.id !== import.meta.env.VITE_EMPTY_GUID && (
                    <Button
                        size="xs"
                        iconLeft={IconClose}
                        view="clear"
                        onClick={onClear}
                        className="dictionary__clearButton items-center justify-center h-auto cursor-pointer text-[#7a9cb1] hover:text-[#1c5682]"
                    />
                )}

                {isCanSearch && !isDisabled && !readonly && (
                    <div
                        className="dictionary__findButton w-[2.5405vmin] h-auto flex items-center justify-center cursor-pointer text-[#7a9cb1] hover:text-[#1c5682]"
                        onClick={() => setIsShowCatalog(true)}
                    >
                        <DictionaryIcon />
                    </div>
                )}

                {isShowCatalog && (
                    <Catalog
                        container={container}
                        onRowClick={onItemClick}
                        onClose={() => setIsShowCatalog(false)}
                        type={value.$type}
                        currentItems={currentItems}
                    />
                )}

                {isShowObject && (
                    <ObjectForm
                        type={value.$type}
                        initialItem={value}
                        container={container}
                        onChange={(value: IBaseObjectDTO) => {
                            const newValue = getSimpleObject(value);
                            onChange && onChange(newValue);
                        }}
                        onClose={() => setIsShowObject(false)}
                        {...objectEntity}
                    />
                )}
            </div>
        </div>
    );
};
